// ─── Payslip PDF generator ──────────────────────────────────────────────────
// Builds a one-page A4 payslip ("Fletëpagesa") in the Treasury-style layout that
// mirrors buildEmailHTML() in SalariesList.js (company header, general-data box,
// organization box, two payment columns, Bruto/Neto bars). Used both for the
// email attachment and the employee self-service download, so all three views
// (screen / email / PDF) match field-for-field.

import { jsPDF } from 'jspdf';

const fmtKs = (v) =>
  new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0) + '€';

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const sanitize = (s) => String(s || '').replace(/[^a-zA-Z0-9\-_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');

// Palette (matches the HTML payslip)
const INK     = [15, 23, 42];
const MUTED   = [71, 85, 105];
const FAINT   = [148, 163, 184];
const BORDER  = [157, 180, 204];   // #9DB4CC
const BLUEBAR = [220, 233, 248];   // #DCE9F8
const BLUEBOX = [233, 241, 250];   // #E9F1FA
const HDRBLUE = [31, 58, 95];      // #1F3A5F
const PEACH   = [251, 228, 212];   // #FBE4D4
const PEACHBD = [235, 196, 166];
const PEACHTX = [124, 45, 18];     // #7C2D12
const RULE    = [203, 213, 225];

/**
 * Formati i logos nxirret nga BAJTET, jo nga etiketa e data-URI-t: te kjo bazë
 * logoja ruhet si `data:image/png;base64,/9j/…` — etiketa thotë PNG, përmbajtja
 * është JPEG. jsPDF-ja i beson argumentit dhe do të vizatonte një imazh të
 * thyer pa asnjë gabim.
 */
function logoFormat(dataUrl) {
  const b = String(dataUrl || '').split(',')[1] || '';
  if (b.startsWith('/9j/')) return 'JPEG';
  if (b.startsWith('iVBOR')) return 'PNG';
  return String(dataUrl).includes('image/jpeg') ? 'JPEG' : 'PNG';
}

const MN = ['', 'janar', 'shkurt', 'mars', 'prill', 'maj', 'qershor', 'korrik', 'gusht', 'shtator', 'tetor', 'nëntor', 'dhjetor'];

// Build the jsPDF document (shared by attachment + download).
function buildDoc(worker, listName, company) {
  const gross       = num(worker.Salary ?? worker.salary);
  const adv         = num(worker.Advances ?? worker.advances);
  const extra       = num(worker.HourSalary);
  const bonus       = num(worker.Bonus ?? worker.bonus);
  const pen         = num(worker.Penalties ?? worker.penalties);
  const pension     = num(worker.employeeTaxFee ?? worker.PensionEmployee);
  const pensionEr   = num(worker.employerTaxFee ?? worker.PensionEmployer);
  const tax         = num(worker.taxes ?? worker.Tax);
  const healthIns   = num(worker.HealthInsEmployee);
  const healthInsEr = num(worker.HealthInsEmployer);
  const storedNet   = num(worker.neto ?? worker.NetSalary);
  const net         = storedNet || (gross + extra + bonus - pension - tax - healthIns - adv - pen);
  const totalEarn   = gross + extra + bonus;
  const hourlyRate  = gross ? gross / 173.33 : 0;

  const co     = (company && typeof company === 'object') ? company : { name: company };
  const coName = co.name || co.CompanyName || 'Kompania';
  const coNui  = co.nui || co.NUI || co.FiscalNumber || '';
  const coAddr = [co.address || co.Address || '', co.city || co.City || ''].filter(Boolean).join(', ');

  const fullName = worker.FullName || `${worker.FirstName || ''} ${worker.LastName || ''}`.trim() || '—';
  const position = worker.Position || worker.position || '';
  const nid      = worker.NID || worker.PersonalNumber || worker.personal_number || '';
  const bank     = worker.BankAccount || worker.bank_account || '';
  const workerNo = worker.WorkerNo || '';
  const contractNo = worker.ContractNo || '';
  const contractTyp = worker.ContractType || '';
  const workHours  = worker.WorkHours || co.workHours || '';
  const organization = worker.Organization || coName;
  const institution  = worker.Institution || '';
  const klasa = worker.Klasa || '';
  const koef  = worker.Koeficienti || '';
  let experience = '';
  const hd = worker.HireDate || worker.hire_date || '';
  if (hd) { const d = new Date(hd); if (!isNaN(d)) { let mo = (new Date().getFullYear() - d.getFullYear()) * 12 + (new Date().getMonth() - d.getMonth()); if (mo < 0) mo = 0; experience = `${Math.floor(mo / 12)} vite, ${mo % 12} muaj`; } }

  const mNum = parseInt(worker.Month, 10) || 0;
  const yNum = parseInt(worker.Year, 10) || 0;
  const periodLabel = (mNum >= 1 && mNum <= 12 && yNum)
    ? `1 ${MN[mNum]} ${yNum} - ${new Date(yNum, mNum, 0).getDate()} ${MN[mNum]} ${yNum}`
    : String(listName || '');

  const earnings = [
    { label: 'Paga bazë bruto',      v: gross,      always: true },
    { label: 'Çmimi i orës (€/orë)', v: hourlyRate, always: true },
    { label: 'Orë shtesë',           v: extra },
    { label: 'Bonus',                v: bonus },
  ].filter((r) => r.always || r.v > 0);
  const deductions = [
    { label: 'Tatimi mbi të ardhura',       v: tax },
    { label: 'Sig. pensionale (punëtori)',  v: pension },
    { label: 'Sig. shëndetësor (punëtori)', v: healthIns },
    { label: 'Avanci',                      v: adv },
    { label: 'Penalitete',                  v: pen },
  ].filter((r) => r.v > 0);
  const employer = [
    { label: 'Pension (punëdhënësi)',          v: pensionEr },
    { label: 'Sig. shëndetësor (punëdhënësi)', v: healthInsEr },
  ].filter((r) => r.v > 0);

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const M = 14;
  const CW = W - M * 2;
  const colW = (CW - 6) / 2;
  const dash = (v) => (v || v === 0 ? String(v) : '—');
  let y = 18;

  // ── Header (centered) ──
  doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(...INK);
  // Logoja e kompanisë mbi emrin — fletëpagesa del në emrin e punëdhënësit.
  // Përmasat vijnë nga vetë skedari, që marka të mos shtrembërohet; nëse
  // vizatimi dështon, mbetet vetëm emri e jo fletëpagesa bosh.
  const coLogo = co.logo || co.Logo || '';
  if (coLogo) {
    try {
      const props = doc.getImageProperties(coLogo);
      const lh = 13;
      const lw = Math.min(lh * (props.width / props.height), 46);
      doc.addImage(coLogo, logoFormat(coLogo), (W - lw) / 2, y - 4, lw, lh);
      y += lh;
    } catch { /* pa logo */ }
  }
  doc.text(coName, W / 2, y, { align: 'center' });
  y += 5;
  if (coNui || coAddr) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...MUTED);
    doc.text([coNui ? `NUI: ${coNui}` : '', coAddr].filter(Boolean).join(' · '), W / 2, y, { align: 'center' });
    y += 4.5;
  }
  doc.setFont('helvetica', 'italic'); doc.setFontSize(9); doc.setTextColor(...FAINT);
  doc.text('Fletëpagesë / Pay slip', W / 2, y, { align: 'center' });
  y += 4;
  doc.setDrawColor(...RULE); doc.line(M, y, W - M, y);
  y += 7;

  // ── Period bar ──
  doc.setFillColor(...BLUEBAR); doc.setDrawColor(...BORDER);
  doc.roundedRect(M, y, CW, 9, 1.5, 1.5, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...HDRBLUE);
  doc.text(`Pagesa për periudhën ${periodLabel}`, W / 2, y + 6, { align: 'center' });
  y += 15;

  // ── Të dhëna gjenerale ──
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...INK);
  doc.text('Të dhëna gjenerale', M, y);
  y += 3;
  const genH = 32;
  doc.setDrawColor(...BORDER); doc.setFillColor(255, 255, 255);
  doc.roundedRect(M, y, CW, genH, 1.5, 1.5, 'FD');
  doc.line(M + colW + 3, y + 2, M + colW + 3, y + genH - 2);
  const field = (label, value, x, fy) => {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...MUTED);
    doc.text(`${label}:`, x, fy);
    doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
    doc.text(doc.splitTextToSize(dash(value), colW - 36), x + 32, fy);
  };
  const lx = M + 5; const rx = M + colW + 8; let fy = y + 7;
  const L = [['Nëpunësi', fullName], ['Numri personal', nid], ['Numri i punëtorit', workerNo], ['Numri i kontratës', contractNo]];
  const R = [['Orët e punës', workHours], ['Lloji i kontratës', contractTyp], ['Banka', co.bank || ''], ['Numri i llogarisë', bank]];
  L.forEach((f, i) => field(f[0], f[1], lx, fy + i * 6.4));
  R.forEach((f, i) => field(f[0], f[1], rx, fy + i * 6.4));
  y += genH + 5;

  // ── Organizata box ──
  const orgH = 30;
  doc.setDrawColor(...BORDER); doc.setFillColor(255, 255, 255);
  doc.roundedRect(M, y, CW, orgH, 1.5, 1.5, 'FD');
  field('Organizata', organization, M + 5, y + 7);
  field('Institucioni', institution, M + 5, y + 13.4);
  field('Emërtimi i pozitës', position, M + 5, y + 19.8);
  doc.setDrawColor(...RULE); doc.line(M + 3, y + 23, M + CW - 3, y + 23);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...MUTED);
  doc.text('Klasa:', M + 5, y + 27.5);
  doc.text('Koeficienti:', W / 2 - 14, y + 27.5);
  doc.text('Përvoja:', W - M - 42, y + 27.5);
  doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
  doc.text(dash(klasa), M + 18, y + 27.5);
  doc.text(dash(koef), W / 2 + 6, y + 27.5);
  doc.text(dash(experience), W - M - 26, y + 27.5);
  y += orgH + 6;

  // ── Detajet e pagesës ──
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...INK);
  doc.text('Detajet e pagesës', M, y);
  y += 3;
  const payCol = (x, title, rows) => {
    const bodyH = 11 + rows.length * 6 + 2;
    doc.setDrawColor(...BORDER); doc.setFillColor(...BLUEBOX);
    doc.roundedRect(x, y, colW, bodyH, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...HDRBLUE);
    doc.text(title, x + 4, y + 7);
    doc.setDrawColor(...[195, 212, 230]); doc.line(x, y + 10, x + colW, y + 10);
    let ry = y + 16;
    rows.forEach((r) => {
      const muted = !(r.v > 0 || r.always);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...(muted ? FAINT : MUTED));
      doc.text(`- ${r.label}`, x + 4, ry);
      const t = fmtKs(r.v);
      doc.setFont('helvetica', 'bold'); doc.setTextColor(...(muted ? FAINT : INK));
      doc.text(t, x + colW - 4 - doc.getTextWidth(t), ry);
      ry += 6;
    });
    return y + bodyH;
  };
  const yL = payCol(M, 'Paga / shtesat / kompenzimet', earnings);
  const yR = payCol(M + colW + 6, 'Ndalesat', deductions);
  y = Math.max(yL, yR) + 5;

  // ── Employer contributions ──
  if (employer.length) {
    const eH = 9 + employer.length * 6;
    doc.setDrawColor(...[217, 226, 236]); doc.setFillColor(246, 249, 252);
    doc.roundedRect(M, y, CW, eH, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...FAINT);
    doc.text('KONTRIBUTET E PUNËDHËNËSIT (INFORMATIVE)', M + 4, y + 6);
    let ry = y + 12;
    employer.forEach((r) => {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...MUTED);
      doc.text(`- ${r.label}`, M + 4, ry);
      const t = fmtKs(r.v);
      doc.setFont('helvetica', 'bold'); doc.setTextColor(...MUTED);
      doc.text(t, M + CW - 4 - doc.getTextWidth(t), ry);
      ry += 6;
    });
    y += eH + 5;
  }

  // ── Bruto / Neto bars ──
  const bar = (label, value) => {
    doc.setFillColor(...PEACH); doc.setDrawColor(...PEACHBD);
    doc.roundedRect(M, y, CW, 12, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...PEACHTX);
    doc.text(`${label}: ${fmtKs(value)}`, W / 2, y + 8, { align: 'center' });
    y += 15;
  };
  bar('Paga bruto', totalEarn);
  bar('Paga neto', net);

  // ── Footer ──
  y += 2;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...FAINT);
  doc.text('Faqe 1 prej 1', M, y);
  let genDate = '';
  try { genDate = `${new Date().toLocaleDateString('sq-AL')} ${new Date().toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' })}`; } catch (_) { genDate = ''; }
  doc.text(genDate, W - M, y, { align: 'right' });

  const filename = `Fletepagesa-${sanitize(fullName) || 'punetor'}-${sanitize(listName) || 'periudha'}.pdf`;
  return { doc, filename };
}

// Email attachment (base64) for notificationsService.sendEmail.
export function buildPayslipPdfAttachment(worker, listName, company) {
  try {
    const { doc, filename } = buildDoc(worker, listName, company);
    const base64 = doc.output('datauristring').split(',')[1];
    return { filename, name: filename, content: base64, encoding: 'base64', contentType: 'application/pdf' };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[payslipPdf] generation failed:', e);
    return null;
  }
}

// Trigger a direct browser download of the same PDF (employee self-service).
export function downloadPayslipPdf(worker, listName, company) {
  try {
    const { doc, filename } = buildDoc(worker, listName, company);
    doc.save(filename);
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[payslipPdf] download failed:', e);
    return false;
  }
}

export default buildPayslipPdfAttachment;
